import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { backdropCloseHandlers } from '../../../../utils/backdropClose';
import { authHeaders } from '../../../../utils/authHeader';
import { baseApi } from '../../../../API/api';
import './ProjectThumbnailModal.css';

// Keep in sync with ALLOWED_EXTENSIONS in Backend/123bimlive/utils/project_thumbnail.py.
export const THUMBNAIL_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.bmp,.tif,.tiff,image/*';
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * Pick an image and upload it as the project's card thumbnail.
 *
 * Visualization projects only (the backend refuses other types). The server
 * converts whatever was picked to produsstorage/{id}_thumb256.jpg and returns
 * a versioned URL that `onUploaded` receives.
 */
const ProjectThumbnailModal = ({
  isOpen,
  projectId,
  projectName = '',
  currentThumbnailUrl = null,
  onCancel,
  onUploaded,
}) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setError('');
      setIsUploading(false);
      setIsDragging(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!isOpen) {
    return null;
  }

  const pick = (candidate) => {
    if (!candidate) return;
    if (!String(candidate.type || '').startsWith('image/')) {
      setError('Please choose an image file (JPG, PNG, GIF, WEBP, BMP or TIFF).');
      return;
    }
    if (candidate.size > MAX_UPLOAD_BYTES) {
      setError(`That image is ${(candidate.size / (1024 * 1024)).toFixed(1)} MB. Please pick one under 25 MB.`);
      return;
    }
    setError('');
    setFile(candidate);
  };

  const handleClose = () => {
    if (isUploading) return;
    onCancel?.();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (isUploading) return;
    pick(event.dataTransfer?.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file || isUploading) return;
    setIsUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      const response = await fetch(`${baseApi}/api/projects/${projectId}/upload/thumbnail`, {
        method: 'POST',
        headers: await authHeaders(),
        body: formData,
      });
      let payload = {};
      try {
        payload = await response.json();
      } catch (parseError) {
        payload = {};
      }
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || payload?.message || `Upload failed (${response.status})`);
      }
      onUploaded?.(payload.thumbnail_url || null);
    } catch (uploadError) {
      setError(uploadError.message || 'Failed to upload thumbnail');
    } finally {
      setIsUploading(false);
    }
  };

  const shownImage = previewUrl || currentThumbnailUrl;

  const modalMarkup = (
    <div
      className="project-thumbnail-modal-overlay"
      {...backdropCloseHandlers(handleClose)}
      role="dialog"
      aria-modal="true"
      aria-label="Change project thumbnail"
    >
      <div className="project-thumbnail-modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="project-thumbnail-modal-header">
          <h3>Project Thumbnail</h3>
          {projectName ? <p>{projectName}</p> : null}
        </div>

        <div
          className={`project-thumbnail-dropzone ${isDragging ? 'is-dragging' : ''} ${shownImage ? 'has-image' : ''}`.trim()}
          onClick={() => !isUploading && inputRef.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            if (!isUploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          aria-label={file ? `Selected ${file.name}. Click to choose another image.` : 'Choose an image'}
        >
          {shownImage ? (
            <img
              src={shownImage}
              alt={file ? 'New thumbnail preview' : 'Current thumbnail'}
              className="project-thumbnail-preview"
            />
          ) : (
            <i className="fas fa-image project-thumbnail-placeholder-icon" aria-hidden="true"></i>
          )}
          <div className="project-thumbnail-dropzone-caption">
            {file ? (
              <>
                <strong>{file.name}</strong>
                <span>{(file.size / 1024).toFixed(0)} KB · click or drop to replace</span>
              </>
            ) : (
              <>
                <strong>{currentThumbnailUrl ? 'Current thumbnail' : 'No thumbnail yet'}</strong>
                <span>Click or drop an image here</span>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={THUMBNAIL_ACCEPT}
            data-testid="project-thumbnail-input"
            style={{ display: 'none' }}
            onChange={(event) => {
              pick(event.target.files?.[0]);
              event.target.value = '';
            }}
            disabled={isUploading}
          />
        </div>

        <p className="project-thumbnail-modal-hint">
          JPG, PNG, GIF, WEBP, BMP or TIFF up to 25 MB. It is converted to JPG and shown at 5:3 on the project card.
        </p>

        {error ? <div className="project-thumbnail-modal-error">{error}</div> : null}

        <div className="project-thumbnail-modal-actions">
          <button
            type="button"
            className="project-thumbnail-modal-btn project-thumbnail-modal-btn-cancel"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`project-thumbnail-modal-btn project-thumbnail-modal-btn-ok ${isUploading ? 'is-saving' : ''}`.trim()}
            onClick={handleUpload}
            disabled={!file || isUploading}
            aria-busy={isUploading ? 'true' : 'false'}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalMarkup, document.body) : modalMarkup;
};

export default ProjectThumbnailModal;
